<script setup lang="ts">
import { ref } from "vue";
import type { Overview } from "../types/api";
import AppIcon from "./AppIcon.vue";
import DailyTraffic from "./DailyTraffic.vue";
import TrafficSources from "./TrafficSources.vue";

defineProps<{ data: Overview; now: number }>();
defineEmits<{ close: [] }>();
const view = ref<"daily" | "sources">("daily");
</script>

<template>
  <div class="traffic-detail fade-in">
    <button class="text-link traffic-back" type="button" @click="$emit('close')">
      <AppIcon name="arrow-left" :size="14" />返回概览
    </button>
    <header class="traffic-title">
      <h1>流量明细</h1>
      <p>每天用了多少，又流向了哪里。</p>
    </header>
    <div class="segmented traffic-tabs" role="group" aria-label="流量视图">
      <button type="button" :aria-pressed="view === 'daily'" @click="view = 'daily'">每日流量</button>
      <button type="button" :aria-pressed="view === 'sources'" @click="view = 'sources'">来源排行</button>
    </div>
    <DailyTraffic v-if="view === 'daily'" :data="data" :now="now" />
    <TrafficSources v-else :data="data" :now="now" />
  </div>
</template>

<style>
/* Shared by the two traffic views. */
.traffic-detail { min-width: 0; padding-block: 1.5rem 3rem; }
.traffic-back { margin-bottom: 1.75rem; }
.traffic-title h1 { font-size: 1.75rem; line-height: 1.3; letter-spacing: -0.01em; }
.traffic-title p { margin-top: 0.35rem; color: var(--ink-3); font-size: 0.9375rem; }
.traffic-tabs { margin-block: 1.75rem 2.25rem; }

.traffic-head { display: flex; flex-wrap: wrap; gap: 1rem 1.5rem; align-items: flex-end; justify-content: space-between; margin-bottom: 1.75rem; }
.traffic-head h2 { font-size: 1.0625rem; }
.traffic-head p { margin-top: 0.25rem; color: var(--ink-3); font-size: 0.8125rem; line-height: 1.6; }
.traffic-controls { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.traffic-controls label { display: grid; gap: 0.3rem; color: var(--ink-3); font-size: 0.75rem; }
.traffic-detail select {
  min-height: 2.25rem;
  padding: 0 2rem 0 0.75rem;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-small);
  background:
    linear-gradient(45deg, transparent 50%, currentColor 50%) right 0.95rem center / 5px 5px no-repeat,
    linear-gradient(-45deg, transparent 50%, currentColor 50%) right 0.65rem center / 5px 5px no-repeat,
    var(--surface);
  color: var(--ink);
  font-size: 0.875rem;
  appearance: none;
}

.traffic-table { overflow-x: auto; }
.traffic-table table { width: 100%; border-collapse: collapse; font-size: 0.875rem; font-variant-numeric: tabular-nums; white-space: nowrap; }
.traffic-table th,
.traffic-table td { padding: 0.7rem 0.75rem; border-bottom: 1px solid var(--rule); text-align: right; }
.traffic-table th:first-child,
.traffic-table td:first-child { padding-left: 0; text-align: left; }
.traffic-table th:last-child,
.traffic-table td:last-child { padding-right: 0; }
.traffic-table thead th { color: var(--ink-3); font-size: 0.75rem; font-weight: 500; border-bottom-color: var(--rule-strong); }
.traffic-table tbody th { font-weight: 400; }
.traffic-table tbody td { color: var(--ink-2); }
.traffic-table tbody td strong { color: var(--ink); font-weight: 600; }
.traffic-table tbody tr:hover { background: var(--wash); }

.traffic-note { margin-top: 1rem; max-width: 46em; color: var(--ink-3); font-size: 0.8125rem; line-height: 1.7; }
.traffic-detail details { margin-top: 1rem; max-width: 52em; color: var(--ink-2); font-size: 0.8125rem; line-height: 1.75; }
.traffic-detail summary { width: fit-content; padding-block: 0.35rem; color: var(--accent-ink); font-weight: 500; cursor: pointer; }
.traffic-detail details p { margin-top: 0.35rem; }
</style>
